/* 
🎯 Shell Design System v3.0 - Theme Provider
Purpose: React context for theme management and dynamic styling
Philosophy: Centralized theme control with automatic CSS variable injection
*/

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { themeConfig, generateCSSVariables, resolveColor } from '../theme.config.js';

// Theme context
const ThemeContext = createContext();

// Hook to use theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme provider component
export const ThemeProvider = ({ 
  children, 
  initialTheme = 'default',
  autoDetectDark = true,
  persistTheme = true 
}) => {
  const [currentTheme, setCurrentTheme] = useState(initialTheme);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isSystemDarkMode, setIsSystemDarkMode] = useState(false);

  // Initialize theme from localStorage and system preferences
  useEffect(() => {
    let savedTheme = initialTheme;
    
    if (persistTheme) {
      const stored = localStorage.getItem('shell-theme');
      if (stored && themeConfig.themes[stored]) {
        savedTheme = stored;
      }
    }
    
    if (autoDetectDark) {
      const systemDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsSystemDarkMode(systemDarkMode);
      
      // If no saved theme and system prefers dark, use dark theme
      if (!localStorage.getItem('shell-theme') && systemDarkMode && themeConfig.themes.dark) {
        savedTheme = 'dark';
      }
    }
    
    setCurrentTheme(savedTheme);
    setIsDarkMode(savedTheme === 'dark');
  }, [initialTheme, autoDetectDark, persistTheme]);

  // Listen for system dark mode changes
  useEffect(() => {
    if (!autoDetectDark) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      setIsSystemDarkMode(e.matches);
      
      // Auto-switch to dark/light if user hasn't manually set a theme
      const hasManualTheme = localStorage.getItem('shell-theme-manual');
      if (!hasManualTheme) {
        const newTheme = e.matches ? 'dark' : 'default';
        if (themeConfig.themes[newTheme]) {
          setCurrentTheme(newTheme);
          setIsDarkMode(newTheme === 'dark');
        }
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [autoDetectDark]);

  // Apply theme to document
  useEffect(() => {
    const cssVariables = generateCSSVariables(currentTheme);
    const rootElement = document.documentElement;
    
    // Apply all CSS custom properties
    Object.entries(cssVariables).forEach(([property, value]) => {
      rootElement.style.setProperty(property, value);
    });
    
    // Set data-theme attribute for CSS selectors
    if (isDarkMode) {
      rootElement.setAttribute('data-theme', 'dark');
    } else {
      rootElement.removeAttribute('data-theme');
    }
    
    // Save to localStorage if persistence is enabled
    if (persistTheme) {
      localStorage.setItem('shell-theme', currentTheme);
    }
    
    // Announce theme change to screen readers
    const announcement = `Theme changed to ${themeConfig.themes[currentTheme]?.name || currentTheme}`;
    announceToScreenReader(announcement);
    
  }, [currentTheme, isDarkMode, persistTheme]);

  // Theme switching functions
  const setTheme = useCallback((themeName) => {
    if (themeConfig.themes[themeName]) {
      setCurrentTheme(themeName);
      setIsDarkMode(themeName === 'dark');
      
      // Mark as manually set theme
      if (persistTheme) {
        localStorage.setItem('shell-theme-manual', 'true');
      }
    } else {
      console.warn(`Theme "${themeName}" not found in theme configuration`);
    }
  }, [persistTheme]);

  const toggleTheme = useCallback(() => {
    const newTheme = isDarkMode ? 'default' : 'dark';
    setTheme(newTheme);
  }, [isDarkMode, setTheme]);

  const resetToSystemTheme = useCallback(() => {
    if (persistTheme) {
      localStorage.removeItem('shell-theme-manual');
    }
    
    const systemTheme = isSystemDarkMode ? 'dark' : 'default';
    if (themeConfig.themes[systemTheme]) {
      setTheme(systemTheme);
    }
  }, [isSystemDarkMode, setTheme, persistTheme]);

  // Get current theme data
  const getThemeData = useCallback(() => {
    return themeConfig.themes[currentTheme];
  }, [currentTheme]);

  // Get resolved color value
  const getColor = useCallback((colorPath) => {
    return resolveColor(colorPath, currentTheme);
  }, [currentTheme]);

  // Get component theme
  const getComponentTheme = useCallback((componentName, variant = 'default') => {
    const componentConfig = themeConfig.components?.[componentName];
    if (componentConfig?.variants?.[variant]) {
      return componentConfig.variants[variant];
    }
    return {};
  }, []);

  // Available themes list
  const availableThemes = Object.keys(themeConfig.themes).map(key => ({
    key,
    name: themeConfig.themes[key].name,
    description: themeConfig.themes[key].description
  }));

  const contextValue = {
    // Current state
    currentTheme,
    isDarkMode,
    isSystemDarkMode,
    
    // Theme data
    themeData: getThemeData(),
    availableThemes,
    
    // Theme switching
    setTheme,
    toggleTheme,
    resetToSystemTheme,
    
    // Utility functions
    getColor,
    getComponentTheme,
    getThemeData,
    
    // Configuration
    config: themeConfig
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// Utility function for screen reader announcements
function announceToScreenReader(message, priority = 'polite') {
  const announcer = document.createElement('div');
  announcer.setAttribute('aria-live', priority);
  announcer.setAttribute('aria-atomic', 'true');
  announcer.className = 'visually-hidden';
  announcer.textContent = message;
  
  document.body.appendChild(announcer);
  
  setTimeout(() => {
    document.body.removeChild(announcer);
  }, 1000);
}

// Higher-order component to inject theme
export const withTheme = (Component) => {
  return function ThemedComponent(props) {
    const theme = useTheme();
    return <Component {...props} theme={theme} />;
  };
};

// Hook for component-specific themes
export const useComponentTheme = (componentName, variant = 'default') => {
  const { getComponentTheme, getColor, currentTheme } = useTheme();
  
  const componentTheme = getComponentTheme(componentName, variant);
  
  // Resolve any color references in the component theme
  const resolvedTheme = Object.entries(componentTheme).reduce((acc, [key, value]) => {
    if (typeof value === 'string' && (value.includes('.') || value.startsWith('grey') || value.startsWith('accent'))) {
      acc[key] = getColor(value);
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});
  
  return {
    ...resolvedTheme,
    theme: currentTheme
  };
};

export default ThemeProvider;

/*
USAGE EXAMPLES:

// 1. Basic theme provider setup (in App.jsx)
import { ThemeProvider } from './shell-design-system';

function App() {
  return (
    <ThemeProvider 
      initialTheme="default"
      autoDetectDark={true}
      persistTheme={true}
    >
      <PlaceholderApp />
    </ThemeProvider>
  );
}

// 2. Using theme in components
import { useTheme } from './shell-design-system';

function MyComponent() {
  const { currentTheme, toggleTheme, getColor, isDarkMode } = useTheme();
  
  return (
    <div style={{ backgroundColor: getColor('accent.primary') }}>
      <h1>Current theme: {currentTheme}</h1>
      <button onClick={toggleTheme}>
        Switch to {isDarkMode ? 'Light' : 'Dark'}
      </button>
    </div>
  );
}

// 3. Using component-specific themes
import { useComponentTheme } from './shell-design-system';

function CustomButton({ variant = 'primary', children, ...props }) {
  const componentTheme = useComponentTheme('Button', variant);
  
  return (
    <button 
      style={{
        backgroundColor: componentTheme.backgroundColor,
        color: componentTheme.color,
        transform: `scale(${componentTheme.hoverScale || 1})`
      }}
      {...props}
    >
      {children}
    </button>
  );
}

// 4. Theme selector component
function ThemeSelector() {
  const { availableThemes, currentTheme, setTheme } = useTheme();
  
  return (
    <select 
      value={currentTheme}
      onChange={(e) => setTheme(e.target.value)}
    >
      {availableThemes.map(theme => (
        <option key={theme.key} value={theme.key}>
          {theme.name}
        </option>
      ))}
    </select>
  );
}

// 5. With HOC pattern
import { withTheme } from './shell-design-system';

const MyThemedComponent = withTheme(({ theme, ...props }) => (
  <div style={{ color: theme.getColor('accent.primary') }}>
    Themed content
  </div>
));

FEATURES:
- Automatic system dark mode detection
- Theme persistence in localStorage
- CSS custom properties injection
- Component-specific theme overrides
- Screen reader announcements
- Manual and automatic theme switching
- Theme validation and error handling
- Utility functions for color resolution

ACCESSIBILITY:
- Screen reader announcements for theme changes
- Respects prefers-color-scheme
- Maintains focus and contrast ratios
- Supports high contrast mode detection

PERFORMANCE:
- Memoized theme switching functions
- Efficient CSS variable updates
- Lazy theme resolution
- Minimal re-renders with context optimization
*/